<template>
  <label
    :class="[
      ns.b('card'),
      ns.is('active', modelValue === label),
      ns.is('disabled', disabled),
      ns.is('focus', focus),
      ns.bm('card', size),
      ns.is('has-icon', icon || $slots.icon),
    ]"
    :style="labelStyle"
    data-component-name="RadioCard"
    :data-event-name="trackEventName"
    :data-event-content="trackEventContent"
  >
  <span v-if="showRadio"
      :class="[
        ns.e('input'),
        ns.is('disabled', disabled),
        ns.is('checked', modelValue === label),
      ]"
    >
      <span :class="ns.e('inner')" />
    </span>
    <jd-icon v-if="icon || $slots.icon" :size="iconSize" :color="iconColor">
      <component :is="icon" v-if="icon"/>
      <slot v-else name="icon" />
    </jd-icon>
    <div :class="ns.be('card', 'content')">
      <input
        ref="radioRef"
        v-model="modelValue"
        :class="ns.be('card', 'original-radio')"
        :value="label"
        type="radio"
        :name="name || radioGroup?.name"
        :disabled="disabled"
        @focus="focus = true"
        @blur="focus = false"
        @click.stop
      />
      <div
        :class="[ns.be('card', 'title'), ns.is('active', modelValue === label)]"
        :style="modelValue === label ? activeStyle : {}"
        @keydown.stop
      >
        <slot>{{ label }}</slot>
      </div>
      <div v-if="showDesc" :class="ns.be('card', 'desc')">
        <span v-if="slots.desc"><slot name="desc" /></span>
        <span v-else>{{ desc }}</span>
      </div>
    </div>
  </label>
</template>

<script lang="ts" setup>
import { computed, useSlots } from 'vue'
import { useNamespace } from '@jdesign-vue/hooks'
import { useRadio } from './use-radio'
import { radioCardProps } from './radio-card'
import type { CSSProperties } from 'vue'

defineOptions({
  name: 'JdRadioCard',
})

const props = defineProps(radioCardProps)

const ns = useNamespace('radio')
const { radioRef, focus, size, disabled, modelValue, radioGroup } =
  useRadio(props)
const activeStyle = computed<CSSProperties>(() => {
  return {
    backgroundColor: radioGroup?.fill || '',
    borderColor: radioGroup?.fill || '',
    boxShadow: radioGroup?.fill ? `-1px 0 0 0 ${radioGroup.fill}` : '',
    color: radioGroup?.textColor,
  }
})
const slots = useSlots()

function getNodeText(node: any): string {
  if (node == null) return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getNodeText).join('')
  return getNodeText(node.children)
}

const trackText = computed(() => {
  const text = getNodeText(slots.default?.() ?? []).trim()
  if (text) return text
  if (props.label != null) return String(props.label)
  return '单选卡片'
})

const isChecked = computed(() => modelValue.value === props.label)

const trackEventName = computed(() =>
  isChecked.value ? 'unchecked' : 'checked'
)

const trackEventContent = computed(() =>
  `${isChecked.value ? '取消选中' : '选中'}${trackText.value}`
)

const labelStyle = computed(() => {
  const icon = slots.icon?.() || []
  const width = props.icon || icon ? Number(props.iconSize) + 144 : 144

  return {
    'max-width': `${props.showRadio ? width + 14 : width}px`,
  }
})
</script>
